import type { MouseEventHandler, ReactNode, RefObject } from "react"

import { Button } from "@coval/ui"
import { Flag, Plus } from "lucide-react"

import { formatTime } from "@/lib/audio-player-utils"

export interface AudioMarker {
  id: string
  time: number
  label: string
  type: "interruption" | "issue" | "note"
}

interface AddAudioMarkerButtonProps {
  visible: boolean
  /**
   * The point on the waveform's top edge the hover is over, in the coordinates
   * of the element this button is positioned against. The bubble centres itself
   * on that point and sits above it, so its arrow lands on the hovered sample.
   */
  position: { x: number; y: number } | null
  onAdd: () => void
  onMouseEnter: MouseEventHandler<HTMLDivElement>
  onMouseLeave: MouseEventHandler<HTMLDivElement>
}

export function AddAudioMarkerButton({
  visible,
  position,
  onAdd,
  onMouseEnter,
  onMouseLeave,
}: AddAudioMarkerButtonProps) {
  if (!visible || !position) {
    return null
  }

  return (
    <div
      className="absolute z-20 -translate-x-1/2 -translate-y-full pb-1"
      style={{
        left: position.x,
        top: position.y,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="relative">
        <Button
          onClick={onAdd}
          className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors"
          aria-label="Add marker"
        >
          <Plus size={16} />
        </Button>
        <div
          className="absolute w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-blue-500"
          style={{
            left: "50%",
            top: "100%",
            transform: "translateX(-50%)",
          }}
        />
      </div>
    </div>
  )
}

interface AudioMarkerFlagsProps {
  markers: AudioMarker[]
  duration: number
  onEdit: (marker: AudioMarker) => void
}

export function AudioMarkerFlags({
  markers,
  duration,
  onEdit,
}: AudioMarkerFlagsProps) {
  return markers.map((marker) => (
    <div
      key={marker.id}
      data-marker-flag
      role="button"
      tabIndex={0}
      className="absolute z-10 bg-red-500 text-white text-xs px-2 py-1 rounded cursor-pointer hover:bg-red-600 transition-colors flag-container"
      style={{
        left: `${(marker.time / (duration || 1)) * 100}%`,
        top: "5px",
        transform: "translateX(-50%)",
      }}
      aria-label={`Edit marker: ${marker.label} at ${formatTime(marker.time)}`}
      onClick={(event) => {
        event.stopPropagation()
        onEdit(marker)
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          event.stopPropagation()
          onEdit(marker)
        }
      }}
    >
      <Flag size={12} />
    </div>
  ))
}

interface AudioRegionSelectionMenuProps {
  selectedRegionId: string | null
  selectedPosition: { x: number; y: number } | null
  clampedPosition: { left: number; top: number } | null
  menuRef: RefObject<HTMLDivElement | null>
  extra?: (regionId: string) => ReactNode
  onDelete: () => void
  onClear: () => void
}

export function AudioRegionSelectionMenu({
  selectedRegionId,
  selectedPosition,
  clampedPosition,
  menuRef,
  extra,
  onDelete,
  onClear,
}: AudioRegionSelectionMenuProps) {
  if (!selectedRegionId || !selectedPosition) {
    return null
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-30 flex items-center gap-2 bg-white/95 dark:bg-gray-900/95 rounded-md shadow border border-gray-200 dark:border-gray-700 px-2 py-1.5"
      style={
        clampedPosition ?? {
          left: selectedPosition.x + 10,
          top: selectedPosition.y + 10,
        }
      }
    >
      <span className="text-xs text-gray-600 dark:text-gray-400">Selected</span>
      {extra?.(selectedRegionId)}
      <Button
        variant="danger"
        className="h-6 text-xs"
        onClick={onDelete}
        aria-label="Delete highlight"
      >
        Delete
      </Button>
      <Button
        variant="ghost"
        className="h-6 text-xs"
        onClick={onClear}
        aria-label="Clear selection"
      >
        Cancel
      </Button>
    </div>
  )
}
